import React from "react";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import Logo from "src/assets/Logo";
import Login_Bg from "src/assets/Login_Bg";

interface SubscriptionLockedPageProps {
  clientId: number;
  overdueAmount: number;
  overduePeriodDays: number;
  currency: string;
  currencySymbol: string;
  onLogout?: () => void;
}

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
    height: "100vh",
  },
}));

const HeaderStyle = styled("header")(({ theme }) => ({
  top: 0,
  left: 0,
  zIndex: 9,
  lineHeight: 0,
  width: 250,
  height: 80,
  display: "flex",
  alignItems: "center",
  position: "absolute",
  padding: theme.spacing(3),
  justifyContent: "space-between",
  [theme.breakpoints.up("lg")]: {
    alignItems: "flex-start",
  },
  boxShadow: "-3px 7px 40px 19px #0000000D",
  borderBottomRightRadius: "49px",
  backgroundColor: "transparent",
}));

const SubscriptionLockedPage: React.FC<SubscriptionLockedPageProps> = ({
  clientId,
  overdueAmount,
  overduePeriodDays,
  currency,
  currencySymbol,
  onLogout,
}) => {
  const isUpLg = useMediaQuery((theme: any) => theme.breakpoints.up("lg"));

  const handlePayNow = () => {
    const paymentUrl = process.env.REACT_APP_API_VEHICLE_BOOKING_URL;
    const cleanUrl = paymentUrl?.replace("/api/vehicle", "") || "";
    window.location.href = `${cleanUrl}payment-portal?clientId=${clientId}&subscriptionType=2`;
  };

  return (
    <RootStyle>
      <HeaderStyle>
        <Logo width={250} height={50} />
      </HeaderStyle>
      <Grid container>
        <Grid
          item
          xl={3}
          lg={3}
          md={12}
          xs={12}
          component="main"
          maxWidth="xs"
          sx={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginLeft: { lg: "5%", xs: "0%" },
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: "primary.main",
              padding: "30px",
              borderRadius: "10px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              width: "400px",
              color: "primary.contrastText",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px auto",
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 32, color: "secondary.main" }} />
            </Box>

            <Typography
              component="h1"
              variant="h5"
              sx={{ color: "primary.contrastText", fontWeight: "bold", mb: 1 }}
            >
              Subscription Locked
            </Typography>

            <Typography
              sx={{
                color: "primary.contrastText",
                fontSize: "14px",
                opacity: 0.8,
                mb: 3,
                lineHeight: 1.5,
              }}
            >
              Access is suspended because subscription payment is overdue by 3+ months.
            </Typography>

            <Box
              sx={{
                background: "rgba(255, 255, 255, 0.05)",
                borderRadius: "8px",
                padding: 2,
                border: "1px solid rgba(255, 255, 255, 0.1)",
                mb: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "12px", opacity: 0.6, mb: 0.5 }}>
                  Overdue Period
                </Typography>
                <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "secondary.main" }}>
                  {overduePeriodDays} Days
                </Typography>
              </Box>
              <Box sx={{ width: "1px", height: "30px", background: "rgba(255, 255, 255, 0.2)" }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "12px", opacity: 0.6, mb: 0.5 }}>
                  Total Payment Due
                </Typography>
                <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "secondary.main" }}>
                  {currencySymbol}
                  {overdueAmount.toFixed(2)} {currency}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button
                onClick={handlePayNow}
                fullWidth
                variant="contained"
                color="secondary"
                sx={{
                  fontWeight: "bold",
                  py: 1.2,
                }}
              >
                Pay Now ➝
              </Button>

              {onLogout && (
                <Button
                  onClick={onLogout}
                  fullWidth
                  variant="outlined"
                  startIcon={<ExitToAppIcon />}
                  sx={{
                    color: "primary.contrastText",
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    "&:hover": {
                      borderColor: "primary.contrastText",
                      background: "rgba(255, 255, 255, 0.05)",
                    },
                    fontWeight: "bold",
                    py: 1,
                  }}
                >
                  Back to Login
                </Button>
              )}
            </Box>
          </Box>
        </Grid>

        {isUpLg && (
          <Grid
            xl={9}
            lg={9}
            md={12}
            xs={12}
            item
            sx={{
              height: "100vh",
              backgroundSize: "contain",
              backgroundPosition: "center",
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              overflow: "hidden",
            }}
          >
            <Login_Bg width="100%" height="100vh" />
          </Grid>
        )}
      </Grid>
    </RootStyle>
  );
};

export default SubscriptionLockedPage;
